import {FormControl, FormField} from 'platform/components';
import {Grid} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {PaymentDatesField, resolvePaymentDates} from '@dms/shared';

import {getApiDateString, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {AccountingDocumentFormValues} from '../types';
import {useRecalculateInvoiceItems} from './listOfItems/hooks/useRecalculateInvoiceItems';

export interface CreateInvoiceDatesFieldsProps extends TestIdProps {
  control: FormControl<AccountingDocumentFormValues>;
  formApi: UseFormReturn<AccountingDocumentFormValues>;
  /**
   * A corrective tax document calculates with the corrected document's date of taxable supply,
   * so changing its own date must not trigger a recalculation.
   */
  isCorrectiveTaxDocument?: boolean;
}

/**
 * Must render inside `AccountingCalculationCacheProvider`: changing the date of taxable supply
 * recalculates the invoice items through the shared calculation cache.
 */
export function PaymentDates(props: CreateInvoiceDatesFieldsProps) {
  const {getValues, setValue} = props.formApi;
  const issueDate = useWatch({control: props.control, name: 'issueDate'});

  const [recalculateItems] = useRecalculateInvoiceItems({
    control: props.control,
    formApi: props.formApi,
    listItemType: 'invoices',
  });

  // VAT rates and rounding depend on the date of taxable supply (T20-107266).
  const handleDateOfTaxableSupplyChange = () => {
    if (props.isCorrectiveTaxDocument) {
      return;
    }

    recalculateItems();
  };

  const updateDependentDates = (changedField: PaymentDatesField) => {
    const values = getValues();
    const patch = resolvePaymentDates(changedField, {
      issueDate: parseDate(values.issueDate),
      dueDays: values.dueSinceIssueDateInDays,
      dueDate: parseDate(values.dueDate),
      taxableSupplyDate: parseDate(values.dateOfTaxableSupply),
    });

    if (isNotNil(patch.dueDays)) {
      setValue('dueSinceIssueDateInDays', patch.dueDays);
    }
    if (isNotNil(patch.dueDate)) {
      setValue('dueDate', getApiDateString(patch.dueDate));
    }
  };

  const minDueDate = issueDate ? parseDate(issueDate) : undefined;

  return (
    <Grid columns={4}>
      <FormField
        control={props.control}
        name="issueDate"
        onChange={() => updateDependentDates('issueDate')}
        type="apiDate"
        label={i18n.t('entity.accounting.labels.issueDate')}
        isRequired
        data-testid={suffixTestId('issueDate', props)}
      />
      <FormField
        control={props.control}
        name="dueSinceIssueDateInDays"
        type="number"
        label={i18n.t('entity.accounting.labels.due')}
        onChange={() => updateDependentDates('dueDays')}
        isStepperVisible
        step={1}
        minStepperValue={0}
        isRequired
        data-testid={suffixTestId('dueSinceIssueDateInDays', props)}
      />
      <FormField
        control={props.control}
        name="dueDate"
        type="apiDate"
        label={i18n.t('entity.accounting.labels.dueDate')}
        minDate={minDueDate}
        onChange={() => updateDependentDates('dueDate')}
        isRequired
        data-testid={suffixTestId('dueDate', props)}
      />
      <FormField
        control={props.control}
        name="dateOfTaxableSupply"
        type="apiDate"
        label={i18n.t('entity.accounting.labels.dots')}
        onChange={handleDateOfTaxableSupplyChange}
        isRequired
        data-testid={suffixTestId('dateOfTaxableSupply', props)}
      />
    </Grid>
  );
}
